import { ReloadOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Grid,
  Skeleton,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography
} from "antd"
import type { ColumnsType } from "antd/es/table"
import type { TFunction } from "i18next"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { getAutomationHistory } from "../api/services.ts"
import type {
  Automation,
  AutomationHistory,
  HistoryEnrollment,
  HistoryOccurrence,
  HistoryRun
} from "../types/automation.types"

const { Text } = Typography

// Matches the backend's DRF PAGE_SIZE, which the history endpoint paginates with.
const PAGE_SIZE = 10
// The backend's placeholder when the viewer may not see the lead; shown in
// the interface language instead.
const HIDDEN_LEAD = "Hidden lead"

const TOTALS: (keyof AutomationHistory["totals"])[] = [
  "enrolled",
  "sent",
  "skipped",
  "failed",
  "pending",
  "cancelled"
]

const OCCURRENCE_COLOR: Record<HistoryOccurrence["status"], string> = {
  pending: "blue",
  sending: "processing",
  sent: "green",
  skipped: "orange",
  failed: "red",
  cancelled: "default"
}

const RUN_COLOR: Record<HistoryRun["status"], string> = {
  pending: "blue",
  running: "processing",
  done: "green",
  skipped: "orange",
  cancelled: "default"
}

const ENROLLMENT_COLOR: Record<HistoryEnrollment["status"], string> = {
  active: "blue",
  completed: "green",
  cancelled: "default"
}

const AUDIT_KEYS: Record<string, string> = {
  automation_created: "created",
  automation_enabled: "enabled",
  automation_disabled: "disabled",
  automation_duplicated: "duplicated",
  automation_deleted: "deleted"
}

// A status the backend adds later shows as its raw value rather than a key path.
const statusLabel = (t: TFunction, group: string, status: string) =>
  t(`settingsAutomation.history.${group}.${status}`, { defaultValue: status })

const auditLabel = (
  t: TFunction,
  { event, version }: AutomationHistory["audit"][number]
) => {
  if (event === "automation_updated") {
    return version == null
      ? t("settingsAutomation.history.audit.edited")
      : t("settingsAutomation.history.audit.editedVersion", { version })
  }
  const key = AUDIT_KEYS[event]
  return key ? t(`settingsAutomation.history.audit.${key}`) : event
}

const Dash = () => <Text type={"secondary"}>—</Text>

interface HistoryDrawerProps {
  automation: Automation | null
  onClose: () => void
  /** Runs once the drawer has finished closing, e.g. to hand focus back. */
  afterClose?: () => void
}

export function HistoryDrawer({
  automation,
  onClose,
  afterClose
}: HistoryDrawerProps) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const screens = Grid.useBreakpoint()

  const [history, setHistory] = useState<AutomationHistory | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [activeTab, setActiveTab] = useState("deliveries")

  // Keeps the title while the drawer animates closed.
  const [shown, setShown] = useState<Automation | null>(automation)
  // Only the latest request may write state: switching automations or pages
  // quickly must not let an older response land on top of a newer one.
  const requestRef = useRef(0)

  const load = useCallback(async (id: string, nextPage: number) => {
    const request = ++requestRef.current
    setPage(nextPage)
    setLoading(true)
    setFailed(false)
    let response
    try {
      response = await getAutomationHistory(id, nextPage)
    } catch {
      response = null
    }
    if (request !== requestRef.current) return
    if (response?.ok && response.data) {
      setHistory(response.data)
    } else {
      setFailed(true)
    }
    setLoading(false)
  }, [])

  const automationId = automation?.id
  useEffect(() => {
    if (automation) setShown(automation)
  }, [automation])

  useEffect(() => {
    requestRef.current += 1
    setHistory(null)
    setActiveTab("deliveries")
    if (automationId) void load(automationId, 1)
  }, [automationId, load])

  const occurrenceColumns: ColumnsType<HistoryOccurrence> = [
    {
      key: "sequence",
      title: t("settingsAutomation.history.columns.sequence"),
      align: "center",
      width: 48,
      render: (_, row) => row.sequence_number
    },
    {
      key: "scheduled",
      title: t("settingsAutomation.history.columns.scheduled"),
      align: "center",
      render: (_, row) => formatDate(row.scheduled_at)
    },
    {
      key: "status",
      title: t("settingsAutomation.history.columns.status"),
      align: "center",
      render: (_, row) => (
        <Tag color={OCCURRENCE_COLOR[row.status]}>
          {statusLabel(t, "occurrenceStatus", row.status)}
        </Tag>
      )
    },
    {
      key: "reason",
      title: t("settingsAutomation.history.columns.reason"),
      align: "center",
      render: (_, row) =>
        row.reason_label ? (
          <div className={"flex min-w-0 flex-col"}>
            <Text>{row.reason_label}</Text>
            {row.detail && (
              <Text type={"secondary"} className={"text-xs"}>
                {row.detail}
              </Text>
            )}
          </div>
        ) : (
          <Dash />
        )
    },
    {
      key: "sent_at",
      title: t("settingsAutomation.history.columns.sentAt"),
      align: "center",
      render: (_, row) => (row.sent_at ? formatDate(row.sent_at) : <Dash />)
    },
    {
      key: "recipient",
      title: t("settingsAutomation.history.columns.recipient"),
      align: "center",
      render: (_, row) => row.recipient || <Dash />
    }
  ]

  const enrollmentColumns: ColumnsType<HistoryEnrollment> = [
    {
      key: "lead",
      title: t("settingsAutomation.history.columns.lead"),
      render: (_, row) =>
        row.lead_name === HIDDEN_LEAD ? (
          <Text type={"secondary"}>
            {t("settingsAutomation.history.hiddenLead")}
          </Text>
        ) : (
          <Link to={RoutesPaths.lead.view(row.lead_id)}>{row.lead_name}</Link>
        )
    },
    {
      key: "entered",
      title: t("settingsAutomation.history.columns.entered"),
      align: "center",
      render: (_, row) => formatDate(row.state_entered_at)
    },
    {
      key: "status",
      title: t("settingsAutomation.history.columns.status"),
      align: "center",
      render: (_, row) => {
        return (
          <div className={"flex min-w-0 flex-col items-center gap-1"}>
            <Tag color={ENROLLMENT_COLOR[row.status]}>
              {statusLabel(t, "enrollmentStatus", row.status)}
            </Tag>
            {row.cancel_reason_label && (
              <Text type={"secondary"} className={"text-xs"}>
                {row.cancel_reason_label}
              </Text>
            )}
          </div>
        )
      }
    },
    {
      key: "version",
      title: t("settingsAutomation.history.columns.version"),
      align: "center",
      render: (_, row) => row.version
    }
  ]

  // A Nurturing automation's runs, newest first as the server sends them.
  const runColumns: ColumnsType<HistoryRun> = [
    {
      key: "sequence",
      title: t("settingsAutomation.history.columns.sequence"),
      align: "center",
      width: 48,
      render: (_, row) => row.sequence_number
    },
    {
      key: "scheduled",
      title: t("settingsAutomation.history.columns.scheduled"),
      align: "center",
      render: (_, row) => formatDate(row.scheduled_at)
    },
    {
      key: "status",
      title: t("settingsAutomation.history.columns.status"),
      align: "center",
      render: (_, row) => (
        <div className={"flex min-w-0 flex-col items-center gap-1"}>
          <Tag color={RUN_COLOR[row.status]}>
            {statusLabel(t, "runStatus", row.status)}
          </Tag>
          {row.reason_label && (
            <Text type={"secondary"} className={"text-xs"}>
              {row.reason_label}
            </Text>
          )}
        </div>
      )
    },
    {
      key: "recipients",
      title: t("settingsAutomation.history.recipients"),
      align: "center",
      render: (_, row) =>
        row.status === "done" ? row.recipient_count : <Dash />
    },
    {
      key: "version",
      title: t("settingsAutomation.history.columns.version"),
      align: "center",
      render: (_, row) => row.version
    }
  ]

  const runs = history?.runs ?? []

  const deliveries =
    history && history.enrollments.count === 0 ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("settingsAutomation.history.noEnrollments")}
      />
    ) : (
      <Table<HistoryEnrollment>
        size={"small"}
        rowKey={"id"}
        columns={enrollmentColumns}
        dataSource={history?.enrollments.results ?? []}
        loading={loading}
        scroll={{ x: "max-content" }}
        expandable={{
          rowExpandable: (row) => row.occurrences.length > 0,
          expandedRowRender: (row) => (
            <Table<HistoryOccurrence>
              size={"small"}
              rowKey={"sequence_number"}
              columns={occurrenceColumns}
              dataSource={row.occurrences}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
          )
        }}
        pagination={
          (history?.enrollments.count ?? 0) > PAGE_SIZE && {
            current: page,
            pageSize: PAGE_SIZE,
            total: history?.enrollments.count,
            showSizeChanger: false,
            onChange: (next) => automationId && void load(automationId, next)
          }
        }
      />
    )

  const audit =
    history && history.audit.length === 0 ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("settingsAutomation.history.noChanges")}
      />
    ) : (
      <ul className={"m-0 flex list-none flex-col gap-2 p-0"}>
        {history?.audit.map((entry, index) => (
          <li
            key={`${entry.event}-${entry.occurred_at}-${index}`}
            className={
              "flex flex-wrap items-baseline justify-between gap-x-4 border-b border-gray-100 pb-2"
            }
          >
            <span>
              <Text strong data-testid={"audit-event"}>
                {auditLabel(t, entry)}
              </Text>
              {entry.actor && (
                <Text type={"secondary"}>
                  {" "}
                  {t("settingsAutomation.history.audit.by", {
                    actor: entry.actor
                  })}
                </Text>
              )}
            </span>
            <Text type={"secondary"}>{formatDate(entry.occurred_at)}</Text>
          </li>
        ))}
      </ul>
    )

  const body = () => {
    if (failed && !loading) {
      return (
        <Alert
          type={"error"}
          showIcon
          message={t("settingsAutomation.history.loadError")}
          description={t("settingsAutomation.history.loadErrorBody")}
          action={
            <Button
              icon={<ReloadOutlined />}
              size={"small"}
              onClick={() => automationId && void load(automationId, page)}
            >
              {t("settingsAutomation.history.retry")}
            </Button>
          }
        />
      )
    }
    if (!history) return <Skeleton active />
    return (
      <div className={"flex flex-col gap-4"}>
        <div className={"grid grid-cols-3 gap-4 sm:grid-cols-6"}>
          {TOTALS.map((key) => (
            <Statistic
              key={key}
              title={t(`settingsAutomation.history.totals.${key}`)}
              value={history.totals[key]}
            />
          ))}
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "deliveries",
              label: t("settingsAutomation.history.tabs.deliveries"),
              children: deliveries
            },
            ...(runs.length
              ? [
                  {
                    key: "runs",
                    label: t("settingsAutomation.history.tabs.runs"),
                    children: (
                      <Table<HistoryRun>
                        data-testid={"automation-runs"}
                        size={"small"}
                        rowKey={(row) =>
                          `${row.version}-${row.sequence_number}`
                        }
                        columns={runColumns}
                        dataSource={runs}
                        pagination={false}
                        scroll={{ x: "max-content" }}
                      />
                    )
                  }
                ]
              : []),
            {
              key: "audit",
              label: t("settingsAutomation.history.tabs.audit"),
              children: audit
            }
          ]}
        />
      </div>
    )
  }

  return (
    <Drawer
      open={!!automation}
      onClose={onClose}
      afterOpenChange={(open) => {
        if (!open) afterClose?.()
      }}
      width={screens.md ? 720 : "100%"}
      title={
        shown
          ? t("settingsAutomation.history.title", { name: shown.name })
          : null
      }
    >
      {body()}
    </Drawer>
  )
}
